import { defineEnumClampFunction } from 'src/lib/enum';

export const GENERATION_TOOL_BASIC = {
  basicContentImprover: 'basic_content_improver',
  basicEli5: 'basic_eli5',
  basicSentenceExpander: 'basic_sentence_expander',
  basicAida: 'basic_aida',
  basicPas: 'basic_pas',
  basicPerfectHeadline: 'basic_perfect_headline',
  basicBlogIdeas: 'basic_blog_ideas',
  basicTalkingPointsToParagraph: 'basic_talking_points_to_paragraph',
  basicProductReview: 'basic_product_review',
  summarizer: 'summarizer',
  generalPrompt: 'general_prompt',
} as const;
export type GenerationToolBasic =
  (typeof GENERATION_TOOL_BASIC)[keyof typeof GENERATION_TOOL_BASIC];

// HINT: when adding new tools here, consider adding them to `REWRITE_TO_SUGGEST_TOOL_MAP` as well
export const GENERATION_TOOL_REWRITE = {
  imageImprove: 'image_improve',
  facebookHeadlineProductRewrite: 'dd_facebook_headline_product_rewrite',
  facebookPrimaryTextProductRewrite: 'dd_facebook_primary_text_product_rewrite',
  googleHeadlineProductRewrite: 'dd_google_headline_product_rewrite',
  googleDescriptionProductRewrite: 'dd_google_description_product_rewrite',
  outbrainHeadlineProductRewrite: 'dd_outbrain_headline_product_rewrite',
  taboolaHeadlineProductRewrite: 'dd_taboola_headline_product_rewrite',
  twitterCopyProductRewrite: 'dd_twitter_copy_product_rewrite',
  pinterestDescriptionProductRewrite: 'dd_pinterest_description_product_rewrite',
  landingHeadlineRewrite: 'dd_landing_page_headline_rewrite',
  landingSubheaderRewrite: 'dd_landing_page_subheader_rewrite',
  landingSEOMetadataRewrite: 'dd_landing_page_seo_meta_data_rewrite',
  smsPromotionalRewrite: 'dd_sms_promotional_rewrite',
  linkedinHeadlineSingleImageRewrite: 'dd_linkedin_headline_single_image_rewrite',
  linkedinIntroSingleImageRewrite: 'dd_linkedin_intro_single_image_rewrite',
  instagramCaptionRewrite: 'dd_instagram_caption_rewrite',
  facebookPostRewrite: 'dd_facebook_post_rewrite',
  linkedinPostRewrite: 'dd_linkedin_post_rewrite',
  tweetRewrite: 'dd_tweet_rewrite',
  pinterestPinTitleRewrite: 'dd_pinterest_pin_title_rewrite',
  pinterestPinDescriptionRewrite: 'dd_pinterest_pin_description_rewrite',
  emailSubjectRewrite: 'dd_email_subject_rewrite',
  emailSubjectLineRewrite: 'dd_email_subject_line_rewrite',
  emailBodyRewrite: 'dd_email_body_rewrite',
  emailColdOutreachBodyRewrite: 'dd_email_cold_outreach_body_rewrite',
  emailPromotionalBodyRewrite: 'dd_email_promotional_body_rewrite',
} as const;
export type GenerationToolRewrite =
  (typeof GENERATION_TOOL_REWRITE)[keyof typeof GENERATION_TOOL_REWRITE];

export const GENERATION_TOOL_CUSTOM_MULTI_PROMPT = {
  customMultiPrompt: 'dd_custom_multi_prompt',
} as const;
export type GenerationToolCustomMultiPrompt =
  (typeof GENERATION_TOOL_CUSTOM_MULTI_PROMPT)[keyof typeof GENERATION_TOOL_CUSTOM_MULTI_PROMPT];

export const GENERATION_TOOL_DD = {
  linkedinMixtureAd: 'dd_linkedin_mixture_ad',
  linkedinDocumentAd: 'dd_linkedin_document_ad',
  linkedinVideoAd: 'dd_linkedin_video_ad',
  linkedinTextAd: 'dd_linkedin_text_ad',
  linkedinMessageAd: 'dd_linkedin_message_ad',
  linkedinConversationAd: 'dd_linkedin_conversational_ad',
  linkedinLeadGenFormAd: 'dd_linkedin_lead_gen_form_ad',
  linkedinConversationalAdOpeningMessage: 'dd_linkedin_conversational_ad_opening_message',
  linkedinConversationalAdPrimaryCtaButtonText:
    'dd_linkedin_conversational_ad_primary_cta_button_text',
  linkedinConversationalAdFollowUpMessage: 'dd_linkedin_conversational_ad_follow_up_message',
  linkedinConversationalAdSecondaryCtaButtonText:
    'dd_linkedin_conversational_ad_secondary_cta_button_text',
  linkedinLeadGenFormAdPrimaryText: 'dd_linkedin_lead_gen_form_ad_primary_text',
  linkedinLeadGenFormAdHeadline: 'dd_linkedin_lead_gen_form_ad_headline',
  linkedinLeadGenFormAdOfferHeadline: 'dd_linkedin_lead_gen_form_ad_offer_headline',
  linkedinLeadGenFormAdOfferDetails: 'dd_linkedin_lead_gen_form_ad_offer_details',
  linkedinMessageAdSubject: 'dd_linkedin_message_ad_subject',
  linkedinMessageAdMessage: 'dd_linkedin_message_ad_message',
  linkedinMessageAdCta: 'dd_linkedin_message_ad_cta',
  twitterMixtureAd: 'dd_twitter_mixture_ad',
  pinterestMixtureAd: 'dd_pinterest_mixture_ad',
  facebookMixtureAd: 'dd_facebook_mixture_ad',
  facebookPrimaryTextProduct: 'dd_facebook_primary_text_product',
  facebookHeadlineProduct: 'dd_facebook_headline_product',
  googleAppAd: 'dd_google_app_ad',
  googleDemandGenAd: 'dd_google_demand_gen_ad',
  googleDisplayAd: 'dd_google_display_ad',
  googleShoppingAd: 'dd_google_shopping_ad',
  googleVideoAd: 'dd_google_video_ad',
  googleMediumHeadlineProduct: 'dd_google_medium_headline_product',
  googleDemandGenAdLongHeadlineProduct: 'dd_google_demand_gen_ad_long_headline_product',
  googleDemandGenAdDescriptionProduct: 'dd_google_demand_gen_ad_description_product',
  googleDisplayAdHeadlineProduct: 'dd_google_display_ad_headline_product',
  googleDisplayAdLongHeadlineProduct: 'dd_google_display_ad_long_headline_product',
  googleDisplayAdDescriptionProduct: 'dd_google_display_ad_description_product',
  googleShoppingAdTitle: 'dd_google_shopping_ad_title',
  googleShoppingAdDescription: 'dd_google_shopping_ad_description',
  googleVideoAdHeadlineProduct: 'dd_google_video_ad_headline_product',
  googleVideoAdLongHeadlineProduct: 'dd_google_video_ad_long_headline_product',
  googleVideoAdDescriptionProduct: 'dd_google_video_ad_description_product',
  googleAppAdHeadlineProduct: 'dd_google_app_ad_headline_product',
  googleAppAdDescriptionProduct: 'dd_google_app_ad_description_product',
  outbrainAd: 'dd_outbrain_ad',
  googleRSA: 'dd_google_rsa',
  taboolaAd: 'dd_taboola_ad',
  googlePerformanceMaxAd: 'dd_google_performance_max_ad',
  googleShortDescriptionProduct: 'dd_google_short_description_product',
  googleDescriptionProduct: 'dd_google_description_product',
  googleHeadlineProduct: 'dd_google_headline_product',
  googleLongHeadlineProduct: 'dd_google_long_headline_product',
  googleSearchAdHeadlineProduct: 'dd_google_search_ad_headline_product',
  googleSearchAdDescriptionProduct: 'dd_google_search_ad_description_product',
  outbrainHeadlineProduct: 'dd_outbrain_headline_product',
  outbrainDescriptionProduct: 'dd_outbrain_description_product',
  taboolaHeadlineProduct: 'dd_taboola_headline_product',
  taboolaDescriptionProduct: 'dd_taboola_description_product',
  twitterCopyProduct: 'dd_twitter_copy_product',
  twitterTitleProduct: 'dd_twitter_title_product',
  pinterestDescriptionProduct: 'dd_pinterest_description_product',
  pinterestTitleProduct: 'dd_pinterest_title_product',
  linkedinHeadlineProduct: 'dd_linkedin_headline_product',
  linkedinIntroProduct: 'dd_linkedin_intro_product',

  landingHeadline: 'dd_landing_page_headline',
  landingSubheader: 'dd_landing_page_subheader',
  landingHeader: 'dd_landing_page_header',
  landingCTA: 'dd_landing_page_call_to_action',
  landingBenefits: 'dd_landing_page_benefits',
  landingFeatures: 'dd_landing_page_features',
  landingSEOMetadata: 'dd_landing_page_seo_meta_data',
  landingSEOMetaTitle: 'dd_landing_page_seo_meta_title_tag',
  landingSectionHeadline: 'landing_page_section_headline',

  amazonProductListing: 'dd_amazon_product_listing',
  walmartProductListing: 'dd_walmart_product_listing',
  generalProductListing: 'dd_general_product_listing',

  instagramCaption: 'dd_instagram_caption',
  facebookPost: 'dd_facebook_post',
  tweet: 'dd_tweet',
  linkedinPost: 'dd_linkedin_post',
  youtubeTitle: 'dd_youtube_title',
  youtubeDescription: 'dd_youtube_description',
  pinterestPostDescription: 'dd_pinterest_post_description',
  pinterestPostTitle: 'dd_pinterest_post_title',
  pinterestMixturePost: 'dd_pinterest_mixture_post',
  youtubeMixturePost: 'dd_youtube_mixture_post',

  emailSubject: 'dd_email_subject',
  emailBody: 'dd_email_body',
  emailPromotionalBody: 'dd_email_promotional_body',
  emailColdOutreachSubjectLine: 'dd_email_cold_outreach_subject_line',
  emailColdOutreachBody: 'dd_email_cold_outreach_body',
  emailMixture: 'dd_email_mixture',
  emailHeadline: 'dd_email_headline',
  emailCta: 'dd_email_cta',
  emailIntroParagraph: 'dd_email_intro_paragraph',
  emailSectionHeadline: 'email_section_headline',
  emailHeader: 'dd_email_header',

  smsPromotional: 'dd_sms_promotional',

  linkedinHeadlineSingleImage: 'dd_linkedin_headline_single_image',
  linkedinIntroSingleImage: 'dd_linkedin_intro_single_image',
  linkedinDocumentAdIntro: 'dd_linkedin_document_ad_intro',
  linkedinDocumentAdHeadline: 'dd_linkedin_document_ad_headline',
  linkedinVideoAdIntro: 'dd_linkedin_video_ad_intro',
  linkedinVideoAdHeadline: 'dd_linkedin_video_ad_headline',

  articleBlogHeadline: 'dd_article_blog_headline',

  chromeExtensionExtract: 'dd_chrome_extension_extract',

  // "image" generation tools
  imageWithDescription: 'dd_image_with_description',
  imageByStyle: 'image_by_style',
  imageScorer: 'image_scorer', // special tool, used only in "import section image" long-form flow
  facebookPostImage: 'dd_facebook_post_image',
  linkedinPostImage: 'dd_linkedin_post_image',
  instagramPostImage: 'dd_instagram_post_image',
  pinterestPinImage: 'dd_pinterest_pin_image',
  xPostImage: 'dd_x_post_image',
  youtubeVideoImage: 'dd_youtube_video_image',
  metaAdImage: 'dd_meta_ad_image',
  linkedinSingleImageAdImage: 'dd_linkedin_single_image_ad_image',
  tweeterAdImage: 'dd_tweeter_ad_image',
  pinterestAdImage: 'dd_pinterest_ad_image',
  googleShoppingAdImage: 'dd_google_shopping_ad_image',
  googleAppAdImage: 'dd_google_app_ad_image',
  outbrainAdImage: 'dd_outbrain_ad_image',
  taboolaAdImage: 'dd_taboola_ad_image',
  emailImage: 'dd_email_image',
} as const;
export type GenerationToolDD = (typeof GENERATION_TOOL_DD)[keyof typeof GENERATION_TOOL_DD];

export const GENERATION_TOOL_GENERAL_TEXT = {
  emailGeneralText: 'email_general_text',
  webpageGeneralText: 'webpage_general_text',
  blogPostGeneralText: 'blog_post_general_text',
} as const;
export type GenerationToolGeneralText =
  (typeof GENERATION_TOOL_GENERAL_TEXT)[keyof typeof GENERATION_TOOL_GENERAL_TEXT];

export const GENERATION_TOOL_DD_TEADS = {
  teadsSingleVideoHeadline: 'dd_teads_single_video_headline',
  teadsSingleVideoDescription: 'dd_teads_single_video_description',
} as const;
export type GenerationToolDDTeads =
  (typeof GENERATION_TOOL_DD_TEADS)[keyof typeof GENERATION_TOOL_DD_TEADS];

export const GENERATION_TOOL_FREESTYLE = {
  createYourOwn: 'freestyle_create_your_own',
  freestyle: 'freestyle',
} as const;
export type GenerationToolFreestyle =
  (typeof GENERATION_TOOL_FREESTYLE)[keyof typeof GENERATION_TOOL_FREESTYLE];

export const GENERATION_TOOL_LPCO = {
  lpcoLandingHeadline: 'lpco_landing_page_headline',
  lpcoLandingCTA: 'lpco_landing_page_call_to_action',
  lpcoLandingProductDescription: 'lpco_landing_page_product_description',
  lpcoLandingProductName: 'lpco_landing_page_product_name',
  lpcoLandingCopy: 'lpco_landing_page_copy',
  // this is a purely FE value and won't be accepted by BE as "generation tool" param
  wtmDefault: 'wtm_default',
} as const;
export type GenerationToolLPCO = (typeof GENERATION_TOOL_LPCO)[keyof typeof GENERATION_TOOL_LPCO];

export const GENERATION_TOOL_DEPRECATED = {
  basicLandingPageSeo: 'basic_landing_page_seo',
  basicInstagramSocial: 'basic_instagram_social',
  basicFacebookSocial: 'basic_facebook_social',
  basicTwitterSocial: 'basic_twitter_social',
  basicLinkedinSocial: 'basic_linkedin_social',
  basicYoutubeSocial: 'basic_youtube_social',
  basicEmailSubjectLine: 'basic_email_subject_line',
  basicSms: 'basic_sms',
  basicFacebookAd: 'basic_facebook_ad',
  basicGoogleAd: 'basic_google_ad',
  basicTwitterAd: 'basic_twitter_ad',
  basicLinkedinTextAd: 'basic_linkedin_text_ad',
  basicPinterestAd: 'basic_pinterest_ad',
  basicLandingPage: 'basic_landing_page_builder',
  basicPinterestPinSocial: 'basic_pinterest_pin_social',
  basicColdEmail: 'basic_cold_email',
  basicPromotionalEmail: 'basic_promotional_email',
  linkedinIntroContent: 'dd_linkedin_intro_content',
  linkedinHeadlineContent: 'dd_linkedin_headline_content',
  amazonProductDescription: 'dd_amazon_product_description',
  amazonFeatureList: 'dd_amazon_feature_list',
  walmartProductDescription: 'dd_walmart_product_description',
  walmartFeatureList: 'dd_walmart_feature_list',
  teadsSingleImageHeadline: 'dd_teads_single_image_headline',
  teadsSingleImageDescription: 'dd_teads_single_image_description',
  teadsCarouselHeadline: 'dd_teads_carousel_headline',
  teadsSocialLayout1Headline: 'dd_teads_social_layout_1_headline',
  teadsSocialLayout1PrimaryText: 'dd_teads_social_layout_1_primary_text',
  teadsSocialLayout1Description: 'dd_teads_social_layout_1_description',
  teadsSocialLayout2PrimaryText: 'dd_teads_social_layout_2_primary_text',
  facebookPrimaryTextContent: 'dd_facebook_primary_text_content',
  facebookHeadlineContent: 'dd_facebook_headline_content',
  outbrainHeadlineContent: 'dd_outbrain_headline_content',
  taboolaHeadlineContent: 'dd_taboola_headline_content',
  twitterCopyContent: 'dd_twitter_copy_content',
  pinterestDescriptionContent: 'dd_pinterest_description_content',
  pinterestTitleContent: 'dd_pinterest_title_content',
  basicQuoraAnswers: 'basic_quora_answers',
  basicVideoIdeas: 'basic_video_ideas',
  basicBab: 'basic_bab',
  basicEngagingQuestions: 'basic_engaging_questions',
  basicPersonalBio: 'basic_personal_bio',
  basicCompanyBio: 'basic_company_bio',
  basicFeatureToBenefitsPractical: 'basic_feature_to_benefits_practical',
  basicPersuasiveBulletPoint: 'basic_persuasive_bullet_point',
  basicValueProposition: 'basic_value_proposition',
  basicProductDescriptionList: 'basic_product_description_list',
  basicProductDescriptionRelaxed: 'basic_product_description_relaxed',
  emailSubjectLine: 'dd_email_subject_line',
  emailPromotionalSubjectLine: 'dd_email_promotional_subject_line',
  productLaunchMultiPrompt: 'dd_product_launch_multi_prompt',
  landingPageProductAnnouncementMultiPrompt: 'dd_landing_page_product_announcement_multi_prompt',
  emailDripMultiPrompt: 'dd_email_drip_multi_prompt',
} as const;
export type GenerationToolDeprecated =
  (typeof GENERATION_TOOL_DEPRECATED)[keyof typeof GENERATION_TOOL_DEPRECATED];

export const GENERATION_TOOL_BLOG = {
  blogDescription: 'blog_description',
  blogPostSectionHeadline: 'blog_post_section_headline',
  blogPostSectionSubHeadline: 'blog_post_section_subheadline',
  blogPostIntroParagraph: 'blog_post_intro_paragraph',
} as const;
export type GenerationToolBlog = (typeof GENERATION_TOOL_BLOG)[keyof typeof GENERATION_TOOL_BLOG];

export const GENERATION_TOOL_LONG_FORM = {
  longFormGeneral: 'lf_general',
  longFormBlank: 'lf_blank',
  longFormContentImprover: 'lf_content_improver',
  longFormLocalization: 'lf_localization',
} as const;
export type GenerationToolLongForm =
  (typeof GENERATION_TOOL_LONG_FORM)[keyof typeof GENERATION_TOOL_LONG_FORM];

/**
 * Generation tool
 * @enum {string}
 * @property {GENERATION_TOOL} DEFAULT - default generation tool
 * @property {EnumClampFunction<GENERATION_TOOL>} clamp - function that clamps the value to the enum
 */
export const GENERATION_TOOL = {
  ...GENERATION_TOOL_BASIC,
  ...GENERATION_TOOL_DD,
  ...GENERATION_TOOL_DD_TEADS,
  ...GENERATION_TOOL_FREESTYLE,
  ...GENERATION_TOOL_GENERAL_TEXT,
  ...GENERATION_TOOL_LPCO,
  ...GENERATION_TOOL_REWRITE,
  ...GENERATION_TOOL_CUSTOM_MULTI_PROMPT,
  ...GENERATION_TOOL_DEPRECATED,
  ...GENERATION_TOOL_BLOG,
  ...GENERATION_TOOL_LONG_FORM,
} as const;
export type GenerationTool = (typeof GENERATION_TOOL)[keyof typeof GENERATION_TOOL];
defineEnumClampFunction(GENERATION_TOOL, GENERATION_TOOL.facebookMixtureAd);

export const DEFAULT_GENERATION_TOOL = GENERATION_TOOL.facebookMixtureAd;

export const GENERATION_TOOL_GROUP = {
  customMultiPrompt: 'custom_multi_prompt',
  boost: 'boost',
  localization: 'localization',
  ads: 'ads',
  landingPage: 'landing_page',
  campaigns: 'campaigns',
  social: 'social',
  email: 'email',
  ecommerce: 'ecommerce',
  generalWriting: 'general_writing',
  anyCopy: 'any_copy',
  articleBlog: 'article_blog',
  imageOld: 'image_old',
  images: 'images',
} as const;
export type GenerationToolGroup =
  (typeof GENERATION_TOOL_GROUP)[keyof typeof GENERATION_TOOL_GROUP];

export const GENERATION_TOOL_GROUPS_SORTED: GenerationToolGroup[] = [
  GENERATION_TOOL_GROUP.customMultiPrompt,
  GENERATION_TOOL_GROUP.boost,
  GENERATION_TOOL_GROUP.localization,
  GENERATION_TOOL_GROUP.anyCopy,
  GENERATION_TOOL_GROUP.imageOld,
  GENERATION_TOOL_GROUP.images,
  GENERATION_TOOL_GROUP.social,
  GENERATION_TOOL_GROUP.ads,
  GENERATION_TOOL_GROUP.landingPage,
  GENERATION_TOOL_GROUP.email,
  GENERATION_TOOL_GROUP.ecommerce,
  GENERATION_TOOL_GROUP.generalWriting,
  GENERATION_TOOL_GROUP.articleBlog,
  GENERATION_TOOL_GROUP.campaigns,
];

export const GENERATION_TOOL_BADGE_TYPE = {
  new: 'new',
  improved: 'improved',
} as const;
export type GenerationToolBadgeType =
  (typeof GENERATION_TOOL_BADGE_TYPE)[keyof typeof GENERATION_TOOL_BADGE_TYPE];

export const BLANK_SAVED_PROMPT_ID = 'blank_saved_prompt';

export const GENERATION_TOOLS_INCLUDING_SUB_TEMPLATES_IN_GROUP: Record<string, GenerationTool[]> = {
  [GENERATION_TOOL_GROUP.email]: [GENERATION_TOOL.emailMixture],
};

// User-facing label for the bare freestyle / content-improver variation.
// Prefixed variants (Email/Blog/Webpage text (unscored)) use the same wording
// with their content-type prefix — only the bare unscored case uses this.
export const TEXT_UNSCORED_LABEL = 'Text (unscored)';
